import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';

import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PersonIcon from '@mui/icons-material/Person';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { togglePinnedFiltersState, selectDefleetsPinnedFilters } from '@store';

import { useHasFilters, useAssignees } from 'hooks/common';
import { computeModelData, generateYears, sortArray } from '@utils';
import { vehiclesServices } from '@services';
import { SUBSCRIPTIONS_STATUSES } from '@constants';
import { DEFLEET_TASK_STATUSES_NAMES, TARGET_OPTIONS } from './constants';

const statusOptions = Object.entries(DEFLEET_TASK_STATUSES_NAMES).map(
  ([key, value]) => ({
    text: value.label,
    value: key,
  })
);

const subscriptionStatusOptions = Object.entries(SUBSCRIPTIONS_STATUSES).map(
  ([key, value]) => ({
    text: value,
    value: key,
  })
);

const targetOptions = TARGET_OPTIONS.filter((opt) => opt.value !== '');

const yearOptions = sortArray(generateYears(2015, 1), undefined, 'desc').map(
  (year) => ({
    text: String(year.value ?? year),
    value: String(year.value ?? year),
  })
);

const parseDate = (dateStr) => {
  if (!dateStr || dateStr === '-') return null;
  const [day, month, year] = dateStr.split('/');
  return `${year}-${month}-${day}`;
};

const allFilterOption = { text: 'All', value: '' };

export const useDefleetFilterConfig = ({ defleetTasks } = {}) => {
  const tasks = useMemo(
    () => (Array.isArray(defleetTasks) ? defleetTasks : []),
    [defleetTasks]
  );
  const router = useRouter();
  const dispatch = useDispatch();
  const pinnedFilters = useSelector(selectDefleetsPinnedFilters);
  const { assignees } = useAssignees();

  const vehicleEnumsResponse = useQuery(
    'vehicle-brand-model-enums',
    () => vehiclesServices.getVehicleEnums(),
    { staleTime: 10 * 60 * 1000 }
  );

  const { brandOptions, brandModelMap } = useMemo(() => {
    const models = vehicleEnumsResponse?.data?.data?.models;
    if (!models) return { brandOptions: [], brandModelMap: {} };

    const modelsObjects = computeModelData(models);
    const map = {};
    const options = [];

    modelsObjects?.manufacturers?.forEach((item) => {
      const label = item.text;
      const slug = item.value;
      map[label] = modelsObjects?.modelObj?.[slug] || [];
      options.push({ text: label, value: label });
    });

    return { brandOptions: options, brandModelMap: map };
  }, [vehicleEnumsResponse?.data?.data?.models]);

  const modelOptions = useMemo(() => {
    if (!router.query.brand) return [];
    const brands = router.query.brand.split(',');
    return brands.reduce((acc, brand) => {
      const brandModels = brandModelMap[brand] || [];
      return [...acc, ...brandModels];
    }, []);
  }, [router.query.brand, brandModelMap]);

  const promisedReturnDateOptions = useMemo(() => {
    const unique = new Set();
    const options = [];
    tasks.forEach((task) => {
      const raw = task?.promisedReturnDate;
      if (raw && raw !== '-' && !unique.has(raw)) {
        unique.add(raw);
        const parsed = parseDate(raw);
        if (parsed)
          options.push({ text: raw, value: new Date(parsed).toISOString() });
      }
    });
    return options?.sort((a, b) => new Date(b.value) - new Date(a.value)) || [];
  }, [tasks]);

  const returnDateOptions = useMemo(() => {
    const unique = new Set();
    const options = [];
    tasks.forEach((task) => {
      const raw = task?.returnDate;
      if (raw && raw !== '-' && !unique.has(raw)) {
        unique.add(raw);
        const parsed = parseDate(raw);
        if (parsed)
          options.push({ text: raw, value: new Date(parsed).toISOString() });
      }
    });
    return options?.sort((a, b) => new Date(b.value) - new Date(a.value)) || [];
  }, [tasks]);

  const monthlyFeeOptions = useMemo(() => {
    const unique = new Set();
    const options = [];
    tasks.forEach((task) => {
      const fee = task?.monthlyFee;
      if (fee && fee !== '-' && !unique.has(fee)) {
        unique.add(fee);
        options.push({ text: fee, value: fee });
      }
    });
    return (
      options?.sort((a, b) => parseFloat(a.value) - parseFloat(b.value)) || []
    );
  }, [tasks]);

  const filterCategories = useMemo(
    () => [
      {
        id: 'brand',
        label: 'Brand',
        icon: DirectionsCarIcon,
        options: [allFilterOption, ...brandOptions],
        multiSelect: false,
      },
      {
        id: 'model',
        label: 'Model',
        icon: DirectionsCarIcon,
        options: modelOptions.length ? [allFilterOption, ...modelOptions] : [],
        multiSelect: false,
      },
      {
        id: 'year',
        label: 'Year',
        icon: CalendarTodayIcon,
        options: [allFilterOption, ...yearOptions],
        multiSelect: false,
      },
      {
        id: 'target',
        label: 'Target',
        icon: GpsFixedIcon,
        options: [allFilterOption, ...targetOptions],
        multiSelect: false,
      },
      {
        id: 'status',
        label: 'Defleet Stage',
        icon: RadioButtonUncheckedIcon,
        options: [allFilterOption, ...statusOptions],
        multiSelect: false,
      },
      {
        id: 'promisedReturnDate',
        label: 'Promised Return Date',
        icon: CalendarTodayIcon,
        options: [allFilterOption, ...promisedReturnDateOptions],
        multiSelect: false,
      },
      {
        id: 'returnDate',
        label: 'Return Date',
        icon: CalendarTodayIcon,
        options: [allFilterOption, ...returnDateOptions],
        multiSelect: false,
      },
      {
        id: 'monthlyFee',
        label: 'Monthly Fee',
        icon: AttachMoneyIcon,
        options: [allFilterOption, ...monthlyFeeOptions],
        multiSelect: false,
      },
      {
        id: 'subscriptionStatus',
        label: 'Subscription Status',
        icon: DirectionsBusIcon,
        options: [allFilterOption, ...subscriptionStatusOptions],
        multiSelect: false,
      },
      {
        id: 'assignee',
        label: 'Assignee',
        icon: PersonIcon,
        options: [
          allFilterOption,
          { text: 'None', value: 'None' },
          ...assignees,
        ],
        multiSelect: false,
      },
    ],
    [
      brandOptions,
      modelOptions,
      promisedReturnDateOptions,
      returnDateOptions,
      monthlyFeeOptions,
      assignees,
    ]
  );

  const handlePinnedFilterSelect = (pinnedFilter) => {
    dispatch(togglePinnedFiltersState({ board: 'defleets', pinnedFilter }));
  };

  const { hasFilters } = useHasFilters({
    pinnedFilters,
    omitParams: [
      'page',
      'view',
      'pageSize',
      'order',
      'orderBy',
      'query',
      'viewMode',
      'board',
    ],
  });

  const currentFilters = useMemo(() => {
    const parseQueryValue = (value) => {
      if (!value) return [];
      if (typeof value === 'string') {
        return value.split(',').filter(Boolean);
      }
      return Array.isArray(value) ? value : [];
    };

    return {
      brand: parseQueryValue(router.query.brand),
      model: parseQueryValue(router.query.model),
      year: parseQueryValue(router.query.year),
      status: parseQueryValue(router.query.status),
      target: parseQueryValue(router.query.target),
      subscriptionStatus: parseQueryValue(router.query.subscriptionStatus),
      promisedReturnDate: parseQueryValue(router.query.promisedReturnDate),
      returnDate: parseQueryValue(router.query.returnDate),
      monthlyFee: parseQueryValue(router.query.monthlyFee),
      assignee: parseQueryValue(router.query.assignee),
    };
  }, [router.query]);

  return {
    filterCategories,
    currentFilters,
    pinnedFilters,
    supportPinnedFilters: true,
    hasFilters,
    handlePinnedFilterSelect,
    brandOptions,
    modelOptions,
    yearOptions,
    promisedReturnDateOptions,
    returnDateOptions,
    monthlyFeeOptions,
  };
};
